import { useMemo, useState, useEffect, Fragment } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useReducedMotion } from "framer-motion";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
} from "@/components/ui/carousel";
import { resourcesPage, books, articles, websites, websitesHeading, themes, bookshelf, bookshelfSlides, featuredResources, vandanaShiva } from "@/content/resources";
import { gaia } from "@/content/site";

const slugify = (text) => text.toLowerCase().replace(/[^a-z0-9]+/g, "-");

/** "From the bookshelf" cover slider (Embla via the existing carousel component). */
function BookshelfSlider({ onSelect }) {
  const reduceMotion = useReducedMotion();
  return (
    <div className="mt-10" data-testid="bookshelf-slider">
      <h2 className="font-serif text-2xl md:text-3xl tracking-tight text-charcoal">
        {bookshelf.heading}
      </h2>
      {bookshelf.intro && <p className="mt-2 text-sm md:text-base text-charcoal/80">{bookshelf.intro}</p>}
      <p className="mt-1.5 text-sm italic text-charcoal/60" data-testid="bookshelf-credit">
        Inspired by Schumacher Action Labs.
      </p>
      <Carousel
        opts={{ align: "start", loop: false, containScroll: "trimSnaps", duration: reduceMotion ? 0 : 25 }}
        className="mt-6"
        aria-roledescription="carousel"
        aria-label="Book covers from the reading list"
      >
        <div className="flex justify-end gap-2 mb-4">
          <CarouselPrevious
            data-testid="bookshelf-prev"
            aria-label="Previous book"
            className="static translate-y-0 h-10 w-10 rounded-sm border-charcoal/30 bg-cream text-charcoal hover:bg-sage hover:text-charcoal disabled:opacity-40"
          />
          <CarouselNext
            data-testid="bookshelf-next"
            aria-label="Next book"
            className="static translate-y-0 h-10 w-10 rounded-sm border-charcoal/30 bg-cream text-charcoal hover:bg-sage hover:text-charcoal disabled:opacity-40"
          />
        </div>
        <CarouselContent className="-ml-3">
          {bookshelfSlides.map((slide, i) => (
            <CarouselItem
              key={slide.title}
              className="pl-3 basis-[72%] sm:basis-[46%] lg:basis-[31%]"
              role="group"
              aria-roledescription="slide"
              aria-label={`${i + 1} of ${bookshelfSlides.length}`}
            >
              <button
                type="button"
                onClick={() => onSelect(slide.listTitle)}
                data-testid={`bookshelf-slide-${i + 1}`}
                aria-label={`Jump to ${slide.title} in the reading list`}
                className="group block w-full text-center rounded-sm focus-visible:ring-2 focus-visible:ring-ochre focus-visible:outline-none"
              >
                <img
                  src={slide.cover}
                  alt={`Cover of ${slide.title}`}
                  loading="lazy"
                  className="h-[220px] md:h-[280px] w-auto max-w-full object-contain mx-auto shadow-sm group-hover:shadow-md transition-shadow duration-300"
                />
                <p className="mt-3 font-serif text-base leading-snug text-charcoal group-hover:text-ochre-dark transition-colors duration-300">
                  {slide.title}
                </p>
              </button>
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>
    </div>
  );
}

/** Searchable, theme-filterable reading collection. */
function BooksCollection() {
  const reduceMotion = useReducedMotion();
  const [query, setQuery] = useState("");
  const [theme, setTheme] = useState("All");
  const [highlightId, setHighlightId] = useState(null);

  // Honour direct links to a list entry or featured box, e.g.
  // /resources#book-entry-... or /resources#featured-entry-...
  useEffect(() => {
    const hash = window.location.hash;
    if (!hash.startsWith("#book-entry-") && !hash.startsWith("#featured-entry-")) return;
    const el = document.getElementById(hash.slice(1));
    if (el) {
      setHighlightId(hash.slice(1));
      setTimeout(() => el.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block: "start" }), 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return books.filter((b) => {
      const themeOk = theme === "All" || b.theme === theme;
      const queryOk = !q || b.title.toLowerCase().includes(q);
      return themeOk && queryOk;
    });
  }, [query, theme]);

  const reset = () => {
    setQuery("");
    setTheme("All");
  };

  // Slider clicks: clear any active filters so the entry is visible, then
  // scroll to it and highlight it briefly.
  const revealEntry = (listTitle) => {
    reset();
    const id = `book-entry-${slugify(listTitle)}`;
    setHighlightId(id);
    setTimeout(() => {
      document
        .getElementById(id)
        ?.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block: "center" });
    }, 60);
    setTimeout(() => setHighlightId(null), 2400);
  };

  return (
    <div>
      <h2 className="mt-10 font-serif text-2xl md:text-3xl tracking-tight text-charcoal" data-testid="books-heading">
        {resourcesPage.booksHeading}
      </h2>
      <p className="mt-4 text-base md:text-lg leading-relaxed text-charcoal/90 max-w-2xl">
        {resourcesPage.intro}
      </p>

      <BookshelfSlider onSelect={revealEntry} />

      <div className="mt-6">
        <label htmlFor="books-search" className="text-sm text-charcoal">
          Search titles
        </label>
        <input
          id="books-search"
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          data-testid="books-search-input"
          placeholder="e.g. commons, transition, co-operative…"
          className="mt-2 w-full rounded-sm border border-charcoal/30 bg-cream px-4 py-3 text-base text-charcoal placeholder:text-charcoal/40 focus-visible:ring-2 focus-visible:ring-ochre focus-visible:outline-none"
        />
      </div>

      <div className="mt-5 flex flex-wrap gap-2" role="group" aria-label="Filter by theme" data-testid="theme-filters">
        {["All", ...themes].map((t) => (
          <button
            key={t}
            type="button"
            aria-pressed={theme === t}
            data-testid={`theme-filter-${t === "All" ? "all" : t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            onClick={() => setTheme(t)}
            className={`px-4 py-2 rounded-sm border text-xs md:text-sm transition-colors duration-300 ${
              theme === t
                ? "bg-eucalyptus text-cream border-eucalyptus"
                : "bg-cream text-charcoal border-charcoal/25 hover:border-ochre hover:text-ochre-dark"
            }`}
          >
            {t}
          </button>
        ))}
        {(query || theme !== "All") && (
          <button
            type="button"
            onClick={reset}
            data-testid="books-reset"
            className="px-4 py-2 rounded-sm text-xs md:text-sm text-ochre-dark underline decoration-ochre underline-offset-4 hover:text-charcoal transition-colors duration-300"
          >
            Clear filters
          </button>
        )}
      </div>

      <p className="mt-5 text-sm text-charcoal/60" role="status" aria-live="polite" data-testid="books-count">
        {filtered.length} {filtered.length === 1 ? "title" : "titles"}
        {theme !== "All" ? ` in ${theme}` : ""}
      </p>

      {filtered.length === 0 ? (
        <p className="mt-8 border border-charcoal/15 bg-sage/50 p-6 text-base text-charcoal/85" data-testid="books-no-results">
          {resourcesPage.noResults}
        </p>
      ) : (
        <ul className="mt-4 divide-y divide-charcoal/10 border-y border-charcoal/10" data-testid="book-list">
          {filtered.map((book, i) => {
            const entryId = `book-entry-${slugify(book.title)}`;
            // Break up the long list: key-resource boxes interrupt it at spaced
            // intervals, but only in the unfiltered "All" view.
            const featured =
              !query.trim() && theme === "All"
                ? featuredResources.find((f) => f.afterIndex === i)
                : null;
            return (
              <Fragment key={book.title}>
                <li
                  id={entryId}
                  className={`py-4 px-3 -mx-3 scroll-mt-28 transition-colors duration-700 ${
                    highlightId === entryId ? "bg-ochre/15" : ""
                  }`}
                  data-testid={`book-${slugify(book.title).slice(0, 40)}`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-baseline sm:gap-4">
                    <span className="font-serif text-lg text-charcoal leading-snug">{book.title}</span>
                    <span className="text-sm text-charcoal/50 sm:ml-auto shrink-0 sm:text-right">{book.theme}</span>
                  </div>
                  {book.note && <p className="mt-1.5 text-sm text-charcoal/70">{book.note}</p>}
                  {book.link && (
                    <a
                      href={book.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`book-link-${slugify(book.title).slice(0, 40)}`}
                      className="mt-1.5 inline-block text-sm tracking-wide text-ochre-dark underline decoration-ochre underline-offset-4 hover:text-charcoal transition-colors duration-300"
                    >
                      {book.linkLabel || "Resource page"}
                    </a>
                  )}
                </li>
                {featured && (
                  <li
                    className="py-8 scroll-mt-28"
                    id={`featured-entry-${slugify(featured.title)}`}
                    data-testid={`featured-resource-${slugify(featured.title)}`}
                  >
                    <div
                      className={`border border-ochre/60 bg-sage/50 p-6 md:p-8 ${
                        featured.cover ? "grid grid-cols-1 sm:grid-cols-[10rem_1fr] gap-6 md:gap-8 items-start" : "max-w-2xl"
                      }`}
                    >
                      {featured.cover && (
                        <img
                          src={featured.cover}
                          alt={featured.coverAlt || `Cover of ${featured.title}`}
                          loading="lazy"
                          className="w-40 sm:w-full border border-charcoal/20 shadow-sm"
                        />
                      )}
                      <div>
                        <p className="text-xs uppercase tracking-[0.15em] text-ochre-dark">{featured.label}</p>
                        <h3 className="mt-2 font-serif text-2xl md:text-3xl tracking-tight leading-snug text-charcoal">
                          {featured.title}
                        </h3>
                        {featured.authors && <p className="mt-1 text-sm text-charcoal/60">{featured.authors}</p>}
                        <p className="mt-3 text-base leading-relaxed text-charcoal/90">{featured.note}</p>
                        <a
                          href={featured.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-4 inline-block text-sm tracking-wide text-ochre-dark underline decoration-ochre underline-offset-4 hover:text-charcoal transition-colors duration-300"
                        >
                          {featured.linkLabel}
                        </a>
                      </div>
                    </div>
                  </li>
                )}
              </Fragment>
            );
          })}
        </ul>
      )}

      {/* Articles */}
      <h3 className="mt-12 font-serif text-xl md:text-2xl tracking-tight text-charcoal" data-testid="articles-heading">
        {resourcesPage.articlesHeading}
      </h3>
      <ul className="mt-4 divide-y divide-charcoal/10 border-y border-charcoal/10" data-testid="article-list">
        {articles.map((a) => (
          <li key={a.title} className="py-4 px-3 -mx-3" data-testid={`article-${slugify(a.title).slice(0, 40)}`}>
            <div className="flex flex-col sm:flex-row sm:items-baseline sm:gap-4">
              <span className="font-serif text-lg text-charcoal leading-snug">{a.title}</span>
              <span className="text-sm text-charcoal/50 sm:ml-auto shrink-0 sm:text-right">{a.theme}</span>
            </div>
            {a.note && <p className="mt-1.5 text-sm text-charcoal/70">{a.note}</p>}
            <a
              href={a.link}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`article-link-${slugify(a.title).slice(0, 40)}`}
              className="mt-1.5 inline-block text-sm tracking-wide text-ochre-dark underline decoration-ochre underline-offset-4 hover:text-charcoal transition-colors duration-300"
            >
              {a.linkLabel}
            </a>
          </li>
        ))}
      </ul>

      {/* Websites */}
      <h3 className="mt-12 font-serif text-xl md:text-2xl tracking-tight text-charcoal" data-testid="websites-heading">
        {websitesHeading}
      </h3>
      <ul className="mt-4 divide-y divide-charcoal/10 border-y border-charcoal/10" data-testid="website-list">
        {websites.map((w) => (
          <li key={w.title} className="py-4 px-3 -mx-3" data-testid={`website-${slugify(w.title).slice(0, 40)}`}>
            <div className="flex flex-col sm:flex-row sm:items-baseline sm:gap-4">
              <span className="font-serif text-lg text-charcoal leading-snug">{w.title}</span>
              <span className="text-sm text-charcoal/50 sm:ml-auto shrink-0 sm:text-right">{w.theme}</span>
            </div>
            {w.note && <p className="mt-1.5 text-sm text-charcoal/70">{w.note}</p>}
            <a
              href={w.link}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`website-link-${slugify(w.title).slice(0, 40)}`}
              className="mt-1.5 inline-block text-sm tracking-wide text-ochre-dark underline decoration-ochre underline-offset-4 hover:text-charcoal transition-colors duration-300"
            >
              {w.linkLabel}
            </a>
          </li>
        ))}
      </ul>

      {/* Books by Dr. Vandana Shiva */}
      <section className="mt-16 border border-charcoal/15 bg-sage/40 p-7 md:p-10" data-testid="vandana-shiva-section">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.2em] text-ochre-dark" data-testid="vandana-shiva-source">
            {vandanaShiva.source}
          </p>
          <h3 className="mt-2 font-serif text-2xl md:text-3xl tracking-tight leading-snug text-charcoal" data-testid="vandana-shiva-heading">
            {vandanaShiva.heading}
          </h3>
          <p className="mt-3 text-base leading-relaxed text-charcoal/85 max-w-2xl">
            {vandanaShiva.intro}
          </p>
          <p className="mt-5 text-base leading-relaxed text-charcoal/90 max-w-3xl" data-testid="vandana-shiva-bio">
            {vandanaShiva.bio}
          </p>
          <p className="mt-4 text-base leading-relaxed text-charcoal/90 max-w-3xl">
            {vandanaShiva.bijaVidyapeeth}
          </p>
          <a
            href={vandanaShiva.bijaLink}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="vandana-shiva-bija-link"
            className="mt-4 inline-block text-sm tracking-wide text-ochre-dark underline decoration-ochre underline-offset-4 hover:text-charcoal transition-colors duration-300"
          >
            {vandanaShiva.bijaLinkLabel}
          </a>
        </Reveal>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
          {vandanaShiva.shelves.map((shelf, i) => (
            <Reveal key={shelf.label} delay={i * 0.05}>
              <div data-testid={`vandana-shiva-shelf-${i + 1}`}>
                <h4 className="font-serif text-lg text-charcoal leading-snug border-b border-ochre/50 pb-2">
                  {shelf.label}
                </h4>
                <p className="mt-2 text-xs text-charcoal/60 leading-relaxed">{shelf.description}</p>
                <ul className="mt-4 space-y-2.5">
                  {shelf.titles.map((t) => (
                    <li key={t.title} className="text-sm leading-snug text-charcoal/90" data-testid={`vandana-shiva-title-${slugify(t.title).slice(0, 40)}`}>
                      {t.title}
                      {t.year && <span className="text-charcoal/50"> · {t.year}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <div className="mt-10 border border-charcoal/15 bg-sage/50 p-7 md:p-8">
        <p className="font-serif text-xl md:text-2xl text-charcoal" data-testid="resources-closing-prompt">
          {resourcesPage.closingPrompt}
        </p>
        <p className="mt-3 text-base leading-relaxed text-charcoal/85">{resourcesPage.closingBody}</p>
        <Link
          to="/contact"
          data-testid="resources-cta-link"
          className="mt-6 inline-block bg-eucalyptus text-cream px-7 py-3.5 rounded-sm text-sm tracking-wide hover:bg-eucalyptus-deep transition-colors duration-300"
        >
          {resourcesPage.ctaLabel}
        </Link>
      </div>
    </div>
  );
}

const TABS = [
  { id: "books", label: "Reading list" },
  { id: "gaia", label: "Ask Gaia" },
];

export default function Resources() {
  const [searchParams, setSearchParams] = useSearchParams();
  const active = searchParams.get("tab") === "gaia" ? "gaia" : "books";

  const selectTab = (id) => {
    setSearchParams(id === "gaia" ? { tab: "gaia" } : {}, { replace: true });
  };

  return (
    <>
      <Seo
        title="Resources | Bioregional Ventures"
        description="Books exploring land, economics, ecology, community and long-term stewardship — plus Gaia, the Bioregional Ventures AI assistant for community land and enterprise questions."
      />

      <section className="max-w-4xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-20 md:pb-28">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.15em] text-ochre-dark mb-4">Resources</p>
          <h1 className="font-serif text-4xl md:text-5xl tracking-tight leading-tight text-charcoal" data-testid="resources-heading">
            {resourcesPage.title}
          </h1>
        </Reveal>

        <div className="mt-10 flex gap-3 border-b border-charcoal/15" role="tablist" aria-label="Resources sections">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={active === tab.id}
              data-testid={`resources-tab-${tab.id}`}
              onClick={() => selectTab(tab.id)}
              className={`px-5 py-3 text-sm tracking-wide border-b-2 -mb-px transition-colors duration-300 ${
                active === tab.id
                  ? "border-ochre text-ochre-dark"
                  : "border-transparent text-charcoal hover:text-ochre-dark"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {active === "books" && (
          <div role="tabpanel" data-testid="resources-books-panel" id="books" className="scroll-mt-24">
            <BooksCollection />
          </div>
        )}

        {active === "gaia" && (
          <div role="tabpanel" data-testid="resources-gaia-panel">
            <p className="mt-8 text-base leading-relaxed text-charcoal/90 max-w-2xl">{gaia.intro}</p>
            <iframe
              src={gaia.embedUrl}
              title="Gaia - Bioregional Ventures AI assistant"
              data-testid="gaia-iframe"
              className="mt-8 w-full block border-0"
              style={{ borderRadius: "12px", height: "700px" }}
              allow="clipboard-write; microphone; camera"
            />
            <div className="mt-8 border border-charcoal/15 bg-sage/50 p-7 md:p-8">
              <p className="text-base leading-relaxed text-charcoal/90 max-w-2xl">{gaia.inviteBody}</p>
              <Link
                to="/contact"
                data-testid="gaia-invite-button"
                className="mt-6 inline-block bg-eucalyptus text-cream px-7 py-3.5 rounded-sm text-sm tracking-wide hover:bg-eucalyptus-deep transition-colors duration-300"
              >
                {gaia.inviteButton}
              </Link>
              <p className="mt-6 text-xs text-charcoal/60 border-t border-charcoal/10 pt-4" data-testid="gaia-note">
                {gaia.note}
              </p>
            </div>
          </div>
        )}
      </section>
    </>
  );
}
